function base64FromBytes(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  if (typeof globalThis.btoa !== 'function') {
    throw new Error('btoa is not available in this environment');
  }
  return globalThis.btoa(binary);
}

function bytesFromBase64(base64: string): Uint8Array {
  if (typeof globalThis.atob !== 'function') {
    throw new Error('atob is not available in this environment');
  }
  const binary = globalThis.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
export function bytesToBase64Url(bytes: Uint8Array): string {
  if (!bytes || bytes.length === 0) return '';
  const base64 = base64FromBytes(bytes);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function encodeLabelsToBase64Url(
  images: string[],
  labels: Record<string, boolean | null | undefined>
): { encoded: string; numSeconds: number } {
  const numSeconds = images.length;
  if (numSeconds === 0) {
    return { encoded: '', numSeconds: 0 };
  }
  const bytes = new Uint8Array(Math.ceil(numSeconds / 8));
  for (let i = 0; i < numSeconds; i += 1) {
    const key = images[i];
    if (key === undefined) continue;
    if (labels[key]) {
      const byteIndex = Math.floor(i / 8);
      const bitIndex = i % 8;
      const existing = bytes[byteIndex]!;
      bytes[byteIndex] = (existing | (1 << bitIndex)) & 0xff;
    }
  }
  return { encoded: bytesToBase64Url(bytes), numSeconds };
}

export function decodeBase64Url(data: string | null | undefined): Uint8Array {
  if (!data) {
    return new Uint8Array();
  }
  const normalized = data.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
  const base64 = `${normalized}${padding}`;
  return bytesFromBase64(base64);
}

export function decodeVoteToSet(
  vote: {
    encoded?: string | null;
  },
  limit?: number | null
): Set<number> | null {
  const cap = limit != null && Number.isFinite(limit) && limit >= 0 ? Math.floor(limit) : 0;
  if (!vote.encoded || cap <= 0) {
    return new Set<number>();
  }
  try {
    const bytes = decodeBase64Url(vote.encoded);
    const set = new Set<number>();
    for (let i = 0; i < cap; i += 1) {
      const byteIndex = Math.floor(i / 8);
      const bitIndex = i % 8;
      if (byteIndex < bytes.length) {
        const byte = bytes[byteIndex];
        if (byte !== undefined && (byte >> bitIndex) & 1) {
          set.add(i);
        }
      }
    }
    return set;
  } catch {
    // Skip noisy logging for malformed data; caller handles fallback.
  }
  return null;
}

/**
 * Build TAS audit labels with 1-second tolerance.
 *
 * TAS uses bit=0 for "new action" and bit=1 for "ongoing".
 * A disagreement at bit i is tolerated when the same boundary appears within
 * `fps` adjacent positions with opposite polarity (= annotator A marks the
 * boundary up to 1s earlier than B, or vice versa). At fps=1 (legacy) this
 * collapses to the original "[i, i+1]" swap rule; at fps=4 it spans 4 bits
 * (= 1 second of video).
 */
export function buildTasToleranceAuditLabels(
  set1: Set<number>,
  set2: Set<number>,
  totalSeconds: number,
  fps: number = 1
): { labels: Record<string, boolean | null>; visited: Set<number> } {
  const labels: Record<string, boolean | null> = {};
  const visited = new Set<number>();
  const toleranceBits = Math.max(1, Math.floor(fps));

  let i = 0;
  while (i < totalSeconds) {
    const b1 = set1.has(i); // true = ongoing, false = new action
    const b2 = set2.has(i);

    if (b1 === b2) {
      labels[String(i)] = b1;
      visited.add(i);
      i += 1;
      continue;
    }

    // Disagreement at i. Search for a matching swap within `toleranceBits` ahead.
    let tolerated = false;
    for (let k = 1; k <= toleranceBits; k += 1) {
      if (i + k >= totalSeconds) break;
      const b1n = set1.has(i + k);
      const b2n = set2.has(i + k);
      const aEarlier = !b1 && b1n && b2 && !b2n; // A marks new action at i, B at i+k
      const bEarlier = b1 && !b1n && !b2 && b2n; // B marks new action at i, A at i+k
      if (aEarlier || bEarlier) {
        // Fill ALL bits from i to i+k-1 as "ongoing" so encodeLabelsToBase64Url
        // never encounters undefined (= implicit 0 = extra boundary). Only i+k
        // carries the shared "new action" marker.
        for (let j = i; j < i + k; j += 1) {
          labels[String(j)] = true;
          visited.add(j);
        }
        labels[String(i + k)] = false; // shared boundary
        visited.add(i + k);
        tolerated = true;
        i += k + 1;
        break;
      }
    }
    if (!tolerated) {
      labels[String(i)] = null; // genuine conflict
      i += 1;
    }
  }

  return { labels, visited };
}

export function buildBitString(encoded: string, numSeconds: number | null | undefined): string {
  if (!encoded) {
    return '';
  }
  const bytes = decodeBase64Url(encoded);
  const maxBits = bytes.length * 8;
  const limit =
    numSeconds != null && Number.isFinite(numSeconds) && numSeconds >= 0
      ? Math.floor(numSeconds)
      : maxBits;
  if (limit === 0) {
    return '';
  }
  const bits: string[] = [];
  for (let idx = 0; idx < limit; idx += 1) {
    const byteIndex = Math.floor(idx / 8);
    const bitIndex = idx % 8;
    const byte = bytes[byteIndex];
    const bit = byte !== undefined ? (byte >> bitIndex) & 1 : 0;
    bits.push(bit ? '1' : '0');
  }
  return bits.join('');
}
